import { del, get, post, postFile, put } from 'services/request';
import { IChatMessage, IFileNode, IProject, ISession } from './types';

const apiBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const BASE = apiBase ? `${apiBase}/ai-assistant` : '/ai-assistant';

// Projects
export const fetchProjects = () => get<IProject[]>(`${BASE}/projects`);

export const createProject = (data: { name: string }) =>
  post<IProject>(`${BASE}/projects`, data);

export const updateProject = (
  id: number,
  data: { name?: string },
) => put<IProject>(`${BASE}/projects/${id}`, data);

export const deleteProject = (id: number) => del(`${BASE}/projects/${id}`);

// Sessions
export const fetchSessions = (projectId: number) => get<ISession[]>(`${BASE}/projects/${projectId}/sessions`);

export const createSession = (projectId: number, data: { title: string; model: string }) =>
  post<ISession>(`${BASE}/projects/${projectId}/sessions`, data);

export const updateSession = (id: number, data: { title?: string; model?: string; sdkSessionId?: string; projectId?: number }) =>
  put<ISession>(`${BASE}/sessions/${id}`, data);

export const deleteSession = (id: number) => del(`${BASE}/sessions/${id}`);

// Files
const encodePath = (path: string) =>
  path.split('/').map(encodeURIComponent).join('/');

export const fetchFileTree = (projectId: number) =>
  get<IFileNode[]>(`${BASE}/projects/${projectId}/files`, { _t: Date.now() });

export const fetchFileContent = (projectId: number, path: string) =>
  get<{ content: string; type?: string; filename?: string }>(`${BASE}/projects/${projectId}/files/${encodePath(path)}`);

export const updateFileContent = (projectId: number, path: string, content: string) =>
  put(`${BASE}/projects/${projectId}/files/${encodePath(path)}`, { content });

export const deleteFile = (projectId: number, path: string) =>
  del(`${BASE}/projects/${projectId}/files/${encodePath(path)}`);

export const uploadFile = (projectId: number, file: File) =>
  postFile(`${BASE}/projects/${projectId}/files`, file);

export const renameFile = (projectId: number, path: string, newName: string) =>
  post<{ oldPath: string; newPath: string }>(`${BASE}/projects/${projectId}/file-actions/rename`, { path, newName });

export const moveFile = (projectId: number, sourcePath: string, destination: string) =>
  post<{ oldPath: string; newPath: string }>(`${BASE}/projects/${projectId}/file-actions/move`, {
    sourcePath,
    destination,
  });

export const moveFileToProject = (
  projectId: number,
  sourcePath: string,
  targetProjectId: number,
  destination: string = '',
) =>
  post<{ oldPath: string; newPath: string; targetProjectId: number }>(
    `${BASE}/projects/${projectId}/file-actions/move-to-project`,
    { sourcePath, targetProjectId, destination },
  );

export const createDirectory = (projectId: number, path: string) =>
  post<{ path: string }>(`${BASE}/projects/${projectId}/file-actions/mkdir`, { path });

export const deleteDirectory = (projectId: number, path: string) =>
  post(`${BASE}/projects/${projectId}/file-actions/rmdir`, { path });

export const createFile = (projectId: number, path: string) =>
  put(`${BASE}/projects/${projectId}/files/${path}`, { content: '' });

// Messages
export const fetchMessages = (sessionId: number) =>
  get<Array<{ role: string; content: string; timestamp?: string; contextTokens?: number }>>(`${BASE}/sessions/${sessionId}/messages`);

export function mapApiMessages(raw: Array<{ role: string; content: string; timestamp?: string; attachmentPreviews?: string[] }>): IChatMessage[] {
  return raw.map((m, i) => ({
    id: `db-${i}`,
    role: m.role as 'user' | 'assistant',
    content: m.content,
    ...(m.attachmentPreviews ? { attachmentPreviews: m.attachmentPreviews } : {}),
    ...(m.timestamp ? { timestamp: new Date(m.timestamp).getTime() } : {}),
  }));
}

// Attachments
export interface IAttachment {
  name: string;
  data: string; // base64 data URL
}

// SSE Streaming
export interface ISSEEvent {
  type: string;
  content?: any;
  data?: any;
  sessionId?: string;
  summary?: string;
  precedingToolUseIds?: string[];
  toolUseId?: string;
  toolName?: string;
  elapsedTimeSeconds?: number;
  message?: string;
  error?: string;
  resetsAt?: string;
  contextTokens?: number;
}

const NODE_BASE_URL = apiBase ? `${apiBase}/ai-assistant-stream` : 'http://localhost:3100';
const NODE_STREAM_URL = `${NODE_BASE_URL}/chat`;

export function streamChat(
  sessionId: number,
  message: string,
  onEvent: (event: ISSEEvent) => void,
  onDone: () => void,
  onError: (error: Error) => void,
  attachments?: IAttachment[],
): AbortController {
  const controller = new AbortController();

  const run = async () => {
    try {
      // Step 1: Get streaming params from PHP (handles auth + session lookup)
      const configRes = await fetch(`${BASE}/sessions/${sessionId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message }),
        signal: controller.signal,
      });

      if (!configRes.ok) {
        throw new Error(`Chat request failed: ${configRes.status}`);
      }

      const config = await configRes.json();

      if (config.logged === false) {
        throw new Error('UNAUTHENTICATED');
      }

      // Step 2: Stream directly from Node.js (bypasses Apache buffering)
      const response = await fetch(NODE_STREAM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectUuid: config.projectUuid,
          sessionId: config.sdkSessionId,
          message: config.message,
          model: config.model,
          frontendSessionId: sessionId,
          userName: config.userName,
          ...(attachments && attachments.length > 0 ? { attachments } : {}),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const processLines = (lines: string[]): boolean => {
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              onDone();
              return true;
            }
            try {
              const parsed = JSON.parse(data);
              onEvent(parsed);
            } catch {
              // skip malformed JSON
            }
          }
        }
        return false;
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        if (processLines(lines)) return;
      }

      // Process any remaining data left in buffer after stream ends
      if (buffer.trim()) {
        processLines(buffer.split('\n'));
      }

      onDone();
    } catch (err) {
      if ((err as any).name !== 'AbortError') {
        onError(err as Error);
      }
    }
  };

  run();
  return controller;
}

// Check if a session has an active agent
export async function checkSessionActive(sessionId: number): Promise<boolean> {
  try {
    const res = await fetch(`${NODE_BASE_URL}/session-active/${sessionId}`);
    const data = await res.json();
    return data.active === true;
  } catch {
    return false;
  }
}

// Generate title
export async function generateTitle(messages: Array<{ role: string; content: string }>): Promise<{ title: string }> {
  const res = await fetch(`${NODE_BASE_URL}/generate-title`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });
  return res.json();
}

// Auth
export async function authSubmitToken(token: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${NODE_BASE_URL}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  return res.json();
}
